import styled from '@emotion/styled';
import nth from 'lodash/nth';
import React, { useEffect } from 'react';

import { color, font } from 'src/shared/styles/variables';

import {
    Answer,
    FieldType,
    MultiSelectQuestion as IMultiSelectQuestion,
    Question,
} from '../models/assessments';
import { MultiSelectQuestion } from './MultiSelectQuestion';
import { SingleSelectQuestion } from './SingleSelectQuestion';

const PeriodHeader = styled.h3(
    {
        color: color.blueBayoux,
        margin: '1rem',
    },
    font.roman(font.size.medium),
);

const Header = styled.h2(
    {
        color: color.ebonyClay,
        margin: '0 0 30px',
        padding: '0 16px',
    },
    font.black(font.size.extraLarge),
);

const QuestionContainer = styled.div({
    position: 'relative',
    display: 'inline-flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 15,
});

const Underline = styled.span({
    textDecoration: 'underline',
});

interface Props {
    question: IMultiSelectQuestion;
    headerText: string;
    answer: Answer;
    onAnswerChange: (questionId: string, value: number | string | string[]) => void;
    customHeader?: (question: IMultiSelectQuestion) => React.ReactNode;
    onMount: () => void;
}

export const AssessmentQuestion: React.FC<Props> = ({
    question,
    answer,
    onAnswerChange,
    headerText,
    children,
    customHeader,
    onMount,
}) => {
    useEffect(() => {
        if (question) {
            onMount();
        }
    }, [onMount, question]);

    if (!question) {
        return null;
    }

    const shouldDisplayHederText = headerText != null && headerText !== '';
    const headerTextPartToUnderline =
        shouldDisplayHederText && nth(headerText.match(/<underline>([\s\S]*)(?=<\/underline>)/), 1);

    return (
        <>
            {customHeader ? (
                customHeader(question)
            ) : (
                <>
                    {!shouldDisplayHederText ? null : headerTextPartToUnderline ? (
                        <PeriodHeader>
                            {headerText.substring(0, headerText.indexOf('<underline>'))}
                            <Underline>{headerTextPartToUnderline}</Underline>
                            {headerText.substring(
                                headerText.indexOf('</underline>') + 12,
                                headerText.length,
                            )}
                        </PeriodHeader>
                    ) : (
                        <PeriodHeader>{headerText}</PeriodHeader>
                    )}

                    <Header>{question.questionText}</Header>
                </>
            )}

            <QuestionContainer>
                {question.type === FieldType.MultiSelect ? (
                    <MultiSelectQuestion
                        options={(question as Question).responseType}
                        onRateChange={(value: string[]) =>
                            onAnswerChange(question.questionKey, value)
                        }
                        currentRate={(answer as string[]) ?? []}
                    />
                ) : (
                    <SingleSelectQuestion
                        options={(question as Question).responseType}
                        onRateChange={(value: string) =>
                            onAnswerChange(question.questionKey, value)
                        }
                        currentRate={answer as string}
                    />
                )}
                {children}
            </QuestionContainer>
        </>
    );
};
